import {
  Card,
  Box,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import { formatNumber } from "utils/formatting";

type Top1000Counts_T = {
  id: number;
  brand_id: string;
  brand_name: string;
  chain_id: string;
  country_code_list: string;
  total_active_count: number;
};

type Country_T = {
  country_code: string;
  country_name: string;
};

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const Top1000Counts: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [top1000CountsData, setTop1000CountsData] = useState<Top1000Counts_T[]>(
    []
  );
  const [countries, setCountries] = useState<Country_T[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");

  const { showError } = useError();
  const { apiRequest } = useApi();

  const fetchCountries = async () => {
    try {
      const response = await apiRequest<Country_T[]>("/brand_utils/countries", {
        method: "GET",
      });
      if (response.data) {
        const formattedCountries = response.data.map((country) => ({
          country_code: country.country_code,
          country_name: country.country_name,
        }));
        setCountries(formattedCountries);

        if (!selectedCountry) {
          // set default country to US if not already set
          const defaultCountry =
            formattedCountries.find((c) => c.country_code === "us") ||
            formattedCountries[0];
          setSelectedCountry(defaultCountry.country_code);
        }
      } else {
        showError("No data in response");
      }
    } catch (error) {
      showError("Error fetching countries.");
    }
  };

  const fetchTop1000Counts = async () => {
    if (selectedCountry) {
      try {
        setLoading(true); // Set loading to true before the API request
        const response = await apiRequest<Top1000Counts_T[]>(
          `/brand_reports/insights/get_top_1000_counts_by_country/${selectedCountry}`,
          { method: "GET" }
        );
        if (response.data) {
          const formattedData = response.data.map((item) => ({
            ...item,
            total_active_count: Number(item.total_active_count),
          }));
          setTop1000CountsData(formattedData);
        }
      } catch (error) {
        console.error(error);
        showError(`Error fetching branded count by country data.`);
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchTop1000Counts();
  }, [selectedCountry]);

  const columns_def: GridColDef[] = [
    {
      field: "brand_id",
      headerName: "Brand ID",
      width: 150,
    },
    {
      field: "brand_name",
      headerName: "Brand Name",
      width: 250,
    },
    {
      field: "chain_id",
      headerName: "Chain ID",
      width: 250,
    },
    {
      field: "total_active_count",
      headerName: "Total Active Count",
      width: 200,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "country_code_list",
      headerName: "Country Code List",
      flex: 1,
    },
  ];

  return (
    <Grid container spacing={2}>
      {/* Country Select Section */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}></Box>
          <FormControl sx={{ width: 300 }}>
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              labelId="country-select-label"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value as string)}
            >
              {countries.map((country) => (
                <MenuItem
                  key={country.country_code}
                  value={country.country_code}
                >
                  {country.country_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* Data Grid Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <GaneshDataGrid
              loading={loading}
              data={top1000CountsData}
              columns={columns_def}
              gridId="top1000-counts"
              defaultGridState={defaultGridState}
              customHeight="550px"
              showGridSaver={false}
              getRowId={(row: Top1000Counts_T) => row.id}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Top1000Counts;
